import { CircleLazyImage } from '@bilibili-maoer/prelude'
import { api, format } from '@bilibili-maoer/web-utils'

import { useState } from 'react'

import { FormattedTable } from '../../components/table'

import { StyledRouterLink } from '~/core/components/link'
import { Order, TableColumn, TableProps } from '~/features/guild/components/table/types'
import { GuildDataReportListTableRow, GuildDataReportSearchParams } from '~/features/guild/report/types'
import { transformReportQueryParamsToSearchParams } from '~/features/guild/utils'
import { UserApi } from '~/services/fm/user'
import { EGuildRole } from '~/shared/types/services/user'

const TableRowCellBodyHeight = 90

export type GuildReportDataTableProps = {
  reportDataQuery: GuildDataReportSearchParams
  order?: Order
  orderBy?: keyof GuildDataReportListTableRow
  searchCreatorId?: number
  searchCreatorName?: string
  searchAgentName?: string
  handleCreatorIdSearch: (id: number | undefined) => void
  handleCreatorNameSearch: (name: string | undefined) => void
  handleAgentNameSearch: (name: string | undefined) => void
  handleSortChange: (orderBy: keyof GuildDataReportListTableRow) => void
} & Omit<TableProps<GuildDataReportListTableRow>, 'columns'>

export const GuildReportDataTable = (props: GuildReportDataTableProps) => {
  const { data: userGuildRole } = UserApi.getUserGuildRole.useQuery()

  // eslint-disable-next-line no-bitwise
  const isOwner = !!((userGuildRole?.role || 0) & EGuildRole.Owner)

  const handleColumnSort = (column: TableColumn<GuildDataReportListTableRow, {}>) => {
    if (!column.sorter) return
    props.handleSortChange(column.field)
  }

  const [creatorId, setCreatorId] = useState(props.searchCreatorId)
  const [creatorName, setCreatorName] = useState(props.searchCreatorName)
  const [agentName, setAgentName] = useState(props.searchAgentName)

  const handleCreatorIdChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setCreatorId(e.target.value ? parseInt(e.target.value) : undefined)
  }

  const handleCreatorNameChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setCreatorName(e.target.value.trim())
  }

  const handleAgentNameChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    setAgentName(e.target.value.trim())
  }

  const columns: TableColumn<GuildDataReportListTableRow>[] = [
    {
      field: 'live_id',
      headerTitle: '主播 ID',
      headerAlign: 'center',
      headerCellSx: {
        minWidth: '140px',
      },
      bodyHeight: TableRowCellBodyHeight,
      bodyCellAlign: 'center',
      filter: {
        props: {
          label: '主播 ID',
          variant: 'standard',
          value: creatorId || '',
          onChange: handleCreatorIdChange,
          onBlur: () => props.handleCreatorIdSearch(creatorId),
          onKeyDown: (e) => {
            if (e.key === 'Enter') {
              props.handleCreatorIdSearch(creatorId)
            }
          },
          inputProps: {
            inputMode: 'numeric',
            pattern: '[0-9]*',
          },
        },
      },
    },
    {
      field: 'iconurl',
      headerTitle: '主播头像',
      headerAlign: 'center',
      headerCellSx: {
        minWidth: '100px',
      },
      bodyHeight: TableRowCellBodyHeight,
      renderBodyCell: (item) => (
        <a href={`${api.MISSEVAN_LINK_PREFIX_URL}/${item.row.live_id}`} target="_blank">
          <CircleLazyImage
            src={api.removeUrlProtocol(item.row.iconurl)}
            alt={item.row.username}
            width={50}
            height={50}
          />
        </a>
      ),
      bodyCellAlign: 'center',
    },
    {
      field: 'username',
      headerTitle: '主播昵称',
      headerAlign: 'center',
      headerCellSx: {
        minWidth: '160px',
      },
      bodyHeight: TableRowCellBodyHeight,
      bodyCellAlign: 'center',
      filter: {
        props: {
          label: '主播昵称',
          variant: 'standard',
          value: creatorName || '',
          onChange: handleCreatorNameChange,
          onBlur: () => props.handleCreatorNameSearch(creatorName),
          onKeyDown: (e) => {
            if (e.key === 'Enter') {
              props.handleCreatorNameSearch(creatorName)
            }
          },
        },
      },
    },
    {
      field: 'agent_username',
      headerTitle: '经纪人',
      headerAlign: 'center',
      headerCellSx: {
        minWidth: '160px',
      },
      bodyHeight: TableRowCellBodyHeight,
      renderBodyCell: (item) => item.row.agent_username || '-',
      bodyCellAlign: 'center',
      /* eslint-disable prettier/prettier */
      filter: isOwner
        ? {
            props: {
              label: '经纪人',
              variant: 'standard',
              value: agentName || '',
              onChange: handleAgentNameChange,
              onBlur: () => props.handleAgentNameSearch(agentName),
              onKeyDown: (e) => {
                if (e.key === 'Enter') {
                  props.handleAgentNameSearch(agentName)
                }
              },
            },
          }
        : undefined,
      /* eslint-enable prettier/prettier */
    },
    {
      field: 'live_duration',
      headerTitle: '直播时长',
      headerAlign: 'center',
      headerCellSx: {
        minWidth: '120px',
      },
      bodyHeight: TableRowCellBodyHeight,
      renderBodyCell: (item) => format.formatDuration(item.row.live_duration * 60 * 1000, 'zh'),
      bodyCellAlign: 'center',
    },
    {
      field: 'effective_days',
      headerTitle: '有效开播天数',
      headerAlign: 'center',
      headerCellSx: {
        minWidth: '120px',
      },
      bodyHeight: TableRowCellBodyHeight,
      bodyCellAlign: 'center',
    },
    {
      field: 'new_fans_num',
      headerTitle: '新增粉丝数',
      headerAlign: 'center',
      headerCellSx: {
        minWidth: '120px',
      },
      bodyHeight: TableRowCellBodyHeight,
      bodyCellAlign: 'center',
      sorter: {
        order: props.order,
        orderBy: props.orderBy,
        onSort: ({ column }) => {
          handleColumnSort(column)
        },
      },
    },
    {
      field: 'listener_duration_avg',
      headerTitle: '人均收听时长（分钟）',
      headerAlign: 'center',
      headerCellSx: {
        minWidth: '200px',
      },
      bodyHeight: TableRowCellBodyHeight,
      bodyCellAlign: 'center',
      sorter: {
        order: props.order,
        orderBy: props.orderBy,
        onSort: ({ column }) => {
          handleColumnSort(column)
        },
      },
    },
    {
      field: 'listener_duration_more_than_five_minutes_ratio',
      headerTitle: '超 5 分钟收听占比',
      headerAlign: 'center',
      headerCellSx: {
        minWidth: '160px',
      },
      bodyHeight: TableRowCellBodyHeight,
      renderBodyCell: (item) => `${item.row.listener_duration_more_than_five_minutes_ratio}%`,
      bodyCellAlign: 'center',
      sorter: {
        order: props.order,
        orderBy: props.orderBy,
        onSort: ({ column }) => {
          handleColumnSort(column)
        },
      },
    },
    {
      // @ts-expect-error 额外的操作选项
      field: 'detail',
      headerTitle: '操作',
      headerAlign: 'center',
      bodyCellAlign: 'center',
      headerCellSx: {
        width: '100%',
      },
      bodyHeight: TableRowCellBodyHeight,
      pinRight: true,
      renderBodyCell: (item) => (
        <StyledRouterLink
          to={{
            pathname: `/guild/report/${item.row.live_id}`,
            search: transformReportQueryParamsToSearchParams(props.reportDataQuery).toString(),
          }}
          state={{
            iconurl: item.row.iconurl,
            username: item.row.username,
            roomId: item.row.room_id,
          }}
        >
          {' '}
          主播详情
        </StyledRouterLink>
      ),
    },
  ]

  return (
    <FormattedTable
      columns={columns}
      rows={props.rows}
      page={props.page}
      count={props.count}
      onChange={props.onChange}
    />
  )
}
